import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, CheckCircle, ArrowRight, Sparkles, Users, TrendingUp, Shield, Zap, Star } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { productsAPI, clientsAPI, settingsAPI, solutionsAPI, capabilitiesAPI, inquiriesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import SEO from '../components/SEO';

const LandingPage = () => {
  const { language, t } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  
  // State for data from API
  const [bfsiProducts, setBfsiProducts] = useState([]);
  const [industryProducts, setIndustryProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch data from API on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bfsiData, industryData, clientsData, capabilitiesData, solutionsData, settingsData] = await Promise.all([
          productsAPI.getAll('bfsi'),
          productsAPI.getAll('industry'),
          clientsAPI.getAll(),
          capabilitiesAPI.getAll(),
          solutionsAPI.getAll(),
          settingsAPI.getAll()
        ]);
        
        setBfsiProducts(bfsiData);
        setIndustryProducts(industryData);
        setClients(clientsData);
        setCapabilities(capabilitiesData.map(c => c.title));
        setSolutions(solutionsData);
        setSettings(settingsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: language === 'id' ? 'Error' : 'Error',
          description: language === 'id' ? 'Gagal memuat data' : 'Failed to load data',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language, toast]);

  // Handle hash navigation (e.g., #contact, #about) after data loads
  useEffect(() => {
    if (!loading) {
      const hash = window.location.hash;
      if (hash) {
        // Wait for page to fully render, then scroll to hash
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  }, [loading]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await inquiriesAPI.create(formData);
      toast({
        title: t.contact.form.success,
        description: language === 'id' ? 'Kami akan menghubungi Anda segera.' : 'We will contact you soon.'
      });
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast({
        title: language === 'id' ? 'Error' : 'Error',
        description: language === 'id' ? 'Gagal mengirim pesan' : 'Failed to send message',
        variant: 'destructive'
      });
    }
  };

  const getIconComponent = (iconName) => {
    const IconComponent = Icons[iconName.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('')];
    return IconComponent || Icons.Box;
  };

  // Structured data untuk Organization
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Manalogi",
    "alternateName": "PT Manunggal Laju Teknologi",
    "url": window.location.origin,
    "logo": `${window.location.origin}/logo.png`,
    "description": language === 'id' 
      ? "Partner setia dalam transformasi digital yang efektif untuk bisnis Anda"
      : "Your trusted partner in effective digital transformation for your business",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "ID"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": ["Indonesian", "English"]
    },
    "sameAs": []
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        description={language === 'id' 
          ? "Manalogi menyediakan solusi digital transformation terdepan untuk industri BFSI dan berbagai sektor. Tingkatkan efisiensi bisnis Anda dengan teknologi modern."
          : "Manalogi provides leading digital transformation solutions for BFSI and various industries. Improve your business efficiency with modern technology."
        }
        keywords="digital transformation, BFSI solutions, enterprise software, business technology, system integration, Indonesia, software development"
        structuredData={organizationStructuredData}
      />
      <Header />

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-cyan-50 to-teal-50 -z-10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-cyan-200">
                <Sparkles className="w-4 h-4 text-cyan-600" />
                <span className="text-sm font-medium text-gray-700">Digital Transformation Partner</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {t.hero.title}
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                {t.hero.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all"
                >
                  {t.hero.cta}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-600">
                    {settings.stats_years_experience || '15+'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {language === 'id' ? 'Tahun Pengalaman' : 'Years Experience'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-600">
                    {settings.stats_projects_completed || '100+'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {language === 'id' ? 'Proyek Selesai' : 'Projects Completed'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-600">
                    {settings.stats_happy_clients || '50+'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {language === 'id' ? 'Klien Puas' : 'Happy Clients'}
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative z-10 bg-white/50 backdrop-blur-sm p-8 rounded-3xl border border-gray-200 shadow-2xl">
                <img
                  src={settings.hero_image_url || "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80"}
                  alt={settings.hero_image_alt || "Digital Transformation"}
                  className="rounded-2xl w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-gradient-to-br from-cyan-400/20 to-teal-400/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' 
                ? (settings.about_title_id || t.about.title)
                : (settings.about_title_en || t.about.title)}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {language === 'id'
                ? (settings.about_description_id || t.about.description)
                : (settings.about_description_en || t.about.description)}
            </p>
          </div>

          {/* Why Choose Manalogi */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {t.whyChoose.items.map((item, index) => {
              const icons = [Shield, Star, Zap];
              const Icon = icons[index];
              return (
                <Card key={index} className="border-2 hover:border-cyan-500 transition-all hover:shadow-lg group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-cyan-600" />
                    </div>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{item.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t.solutions.title}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => {
              const icons = [TrendingUp, Users, Sparkles];
              const Icon = icons[index % icons.length];
              return (
                <Card key={solution.id} className="bg-white hover:shadow-xl transition-all border-t-4 border-t-cyan-500 group">
                  <CardHeader>
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">
                      {language === 'id' ? solution.title_id : solution.title_en}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed">
                      {language === 'id' ? solution.description_id : solution.description_en}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-4 sm:px-6 lg:px-8 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          {/* BFSI Products */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                {t.products.bfsi.title}
              </h2>
              <p className="text-gray-600">{t.products.bfsi.subtitle}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bfsiProducts.map((product) => {
                const Icon = getIconComponent(product.icon);
                return (
                  <Link 
                    key={product.id} 
                    to={`/product/${product.id}`}
                    className="block h-full"
                  >
                    <Card className="hover:shadow-lg transition-all group cursor-pointer h-full hover:border-cyan-500 border-2 border-transparent">
                      <CardHeader>
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                          <Icon className="w-6 h-6 text-cyan-600" />
                        </div>
                        <CardTitle className="text-base group-hover:text-cyan-600 transition-colors">
                          {language === 'id' ? product.name_id : product.name_en}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-sm">
                          {language === 'id' ? product.description_id : product.description_en}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Industry Products */}
          <div>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                {t.products.industry.title}
              </h2>
              <p className="text-gray-600">{t.products.industry.subtitle}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {industryProducts.map((product) => {
                const Icon = getIconComponent(product.icon);
                return (
                  <Link 
                    key={product.id} 
                    to={`/product/${product.id}`}
                    className="block h-full"
                  >
                    <Card className="hover:shadow-lg transition-all group cursor-pointer h-full hover:border-cyan-500 border-2 border-transparent">
                      <CardHeader>
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                          <Icon className="w-6 h-6 text-teal-600" />
                        </div>
                        <CardTitle className="text-base group-hover:text-cyan-600 transition-colors">
                          {language === 'id' ? product.name_id : product.name_en}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-sm">
                          {language === 'id' ? product.description_id : product.description_en}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 to-teal-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Kemampuan Kami' : 'Our Capabilities'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <CheckCircle className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                <span className="text-gray-700">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-20 px-4 sm:px-6 lg:px-8 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t.clients.title}
            </h2>
            <p className="text-gray-600 mb-6">{t.clients.subtitle}</p>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8">
            {clients.map((client) => (
              <div
                key={client.id}
                className="flex items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200 group"
              >
                <div className="text-center w-full">
                  {client.logo_url ? (
                    <img
                      src={client.logo_url}
                      alt={client.name}
                      className="w-full h-16 object-contain group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-16 flex items-center justify-center">
                      <div className="bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg px-4 py-2 group-hover:scale-105 transition-transform shadow-md">
                        <span className="text-sm font-bold text-white">{client.name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-cyan-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t.contact.title}
            </h2>
            <p className="text-gray-600 mb-6">{t.contact.subtitle}</p>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto"></div>
          </div>

          <Card className="shadow-xl border-t-4 border-t-cyan-500">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.contact.form.name}
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.contact.form.email}
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.contact.form.company}
                  </label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.contact.form.message}
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
                >
                  {t.contact.form.submit}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default LandingPage;
